/**
 * Lykke beside the planner: the brand's account manager on the left of the nine steps (in the new layout, a mode of her
 * panel). Write, dictate, call her, or drop a brief, a PDF, pictures, audio or a video: she fills the steps with what
 * the client said or sent (the server keeps only values their words state) and takes them to the step that shows it.
 *
 * The planner keeps its state in the browser, so her answer is a patch the page applies; the conversation is kept in
 * this browser with the draft.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';
import { AGENT, speechSupported } from '@/cowork/proposals/ProposalBuilder';
import { RealtimeCall } from '@/cowork/proposals/realtimeCall';
import { voiceErrorText } from '@/cowork/voice/voiceSession';
import { AgentComposer, type CallPhase } from '@/cowork/shell/AgentComposer';
import { plannerApi, type LykkeAnswer, type LykkeContext } from './api';
import { STEPS, type Step } from './state';

interface Msg { role: 'lykke' | 'user'; text: string; applied?: LykkeAnswer['applied'] }
const CHAT_KEY = (brandId: string) => `planner-chat-v1:${brandId}`;
// eslint-disable-next-line react-refresh/only-export-components -- the page clears it with the draft
export const clearPlannerChat = (brandId: string) => { try { localStorage.removeItem(CHAT_KEY(brandId)); } catch { /* private mode */ } };

export function PlannerLykke({ brandId, context, opener, onAnswer, onGo, className, inPanel = false }: {
  brandId: string;
  /** The plan as she reads it, fresh at every send (state, step, what is still missing). */
  context: () => Omit<LykkeContext, 'brandId' | 'history'>;
  /** Her first question while the conversation is empty (no call: the first thing still missing). */
  opener: string;
  onAnswer: (patch: Record<string, unknown>) => void;
  onGo: (step: Step) => void;
  className?: string; inPanel?: boolean;
}) {
  const [msgs, setMsgs] = useState<Msg[]>(() => { try { return JSON.parse(localStorage.getItem(CHAT_KEY(brandId)) || '[]'); } catch { return []; } });
  const [busy, setBusy] = useState<null | 'chat' | 'read'>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => { try { localStorage.setItem(CHAT_KEY(brandId), JSON.stringify(msgs.slice(-60))); } catch { /* private mode */ } }, [msgs, brandId]);
  useEffect(() => { threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' }); }, [msgs.length, busy]);

  const apply = (r: Pick<LykkeAnswer, 'patch' | 'go'>) => {
    if (Object.keys(r.patch).length) onAnswer(r.patch);
    if (r.go != null) onGo(r.go as Step);
  };
  const send = (raw: string, files: File[]): boolean | void => {
    if (busy) return false;
    const text = raw.trim(); if (!text && !files.length) return false;
    const reading = files.length > 0 || text.length > 400;
    const history = msgs.map(({ role, text: t }) => ({ role, text: t }));
    setMsgs((m) => [...m, { role: 'user', text: text.length > 400 ? `${text.slice(0, 280)}…` : text || `(Sent ${files.map((f) => f.name).join(', ')})` }]);
    setBusy(reading ? 'read' : 'chat');
    const f = new FormData();
    const c = context();
    Object.entries({ brandId, message: text, history: JSON.stringify(history.slice(-16)), state: JSON.stringify(c.state), step: String(c.step), missing: JSON.stringify(c.missing) }).forEach(([k, v]) => f.append(k, v));
    files.forEach((x) => f.append('files', x, x.name));
    plannerApi.assist(f).then((r) => {
      const say = [r.say, ...(r.notes || [])].filter(Boolean).join(' ');
      setMsgs((m) => [...m, { role: 'lykke', text: say || 'Done.', ...(r.applied.length ? { applied: r.applied } : {}) }]);
      apply(r);
    }).catch((e) => setMsgs((m) => [...m, { role: 'lykke', text: `Sorry, that did not go through: ${(e as Error).message}` }]))
      .finally(() => setBusy(null));
  };

  // ── Live call: Lykke speaks; her tools write through the server, grounded in what was said on the call ──
  const [call, setCall] = useState<{ on: boolean; phase: string; live: string; agentLive: string }>({ on: false, phase: 'idle', live: '', agentLive: '' });
  const rtRef = useRef<RealtimeCall | null>(null);
  const heard = useRef<string[]>([]);
  const ctxRef = useRef(context); ctxRef.current = context;
  const msgsRef = useRef(msgs); msgsRef.current = msgs;
  const startCall = async () => {
    const body = () => ({ brandId, ...ctxRef.current(), history: msgsRef.current.slice(-12).map(({ role, text }) => ({ role, text })) });
    const rt = new RealtimeCall([
      { name: 'set_fields', description: 'Write values the client just said into the planner. Keys are field ids from the catalog; values as said (digits, yyyy-mm-dd, catalog ids). Pass each as {"value": ..., "quote": "<their words>"}.', parameters: { type: 'object', properties: { fields: { type: 'object', additionalProperties: true } }, required: ['fields'] },
        execute: async (a) => {
          const r = await plannerApi.callWrite({ ...body(), fields: a.fields, said: heard.current.slice(-8).join('\n') });
          apply({ patch: r.patch, go: r.go });
          if (r.applied.length) setMsgs((m) => [...m, { role: 'lykke', text: `Wrote ${r.applied.map((x) => x.label).join(', ')}.`, applied: r.applied }]);
          return { saved: r.applied.map((x) => x.label), notSaved: r.rejected, stillMissing: ctxRef.current().missing };
        } },
      { name: 'go_to_step', description: `Show the client a step of the planner: 1 to 9 (${STEPS.join(', ')}).`, parameters: { type: 'object', properties: { step: { type: 'number' } }, required: ['step'] },
        execute: async (a) => { const n = Math.round(Number(a.step)); if (n >= 1 && n <= 9) onGo((n - 1) as Step); return { ok: true }; } },
      { name: 'get_plan', description: 'What the planner holds now, the step on screen and what is still missing.', parameters: { type: 'object', properties: {}, required: [] },
        execute: async () => { const c = ctxRef.current(); return { plan: c.state, step: c.step + 1, stillMissing: c.missing }; } },
    ], {
      onStatus: (st) => { if (st === 'ended' || st === 'error') { setCall({ on: false, phase: 'idle', live: '', agentLive: '' }); rtRef.current = null; return; } setCall((c) => ({ ...c, on: true, phase: st })); },
      onTranscript: (role, t, final) => {
        if (role === 'user') { if (final && t.trim()) { heard.current.push(t.trim()); setMsgs((m) => [...m, { role: 'user', text: t.trim() }]); setCall((c) => ({ ...c, live: '' })); } else setCall((c) => ({ ...c, live: t })); return; }
        if (final) { if (t.trim()) setMsgs((m) => [...m, { role: 'lykke', text: t.trim() }]); setCall((c) => ({ ...c, agentLive: '' })); } else setCall((c) => ({ ...c, agentLive: t }));
      },
      onError: (m) => toast.error(m),
    });
    rtRef.current = rt;
    setCall({ on: true, phase: 'connecting', live: '', agentLive: '' });
    // The offer goes to the mint route: the server connects the call and keeps its voice session (voiceSession.ts).
    try { await rt.connect((sdp) => plannerApi.realtimeSecret({ ...body(), sdp }), undefined, (secret) => ({ instructions: secret.instructions, voice: secret.voice, opening: 'Open the call now: no greeting, ask for the first thing still missing, 20 words at most.' })); }
    catch (e) { rtRef.current = null; setCall({ on: false, phase: 'idle', live: '', agentLive: '' }); toast.error(voiceErrorText(e, 'The call did not connect.')); }
  };
  const endCall = () => { rtRef.current?.end(); rtRef.current = null; setCall({ on: false, phase: 'idle', live: '', agentLive: '' }); };
  useEffect(() => () => { rtRef.current?.end(); }, []);

  const callPhase: CallPhase = !call.on ? 'off' : call.phase === 'connecting' ? 'connecting' : call.phase === 'speaking' ? 'speaking' : call.phase === 'tool' ? 'working' : 'listening';
  const status = call.on ? ({ speaking: 'Talking', listening: 'Listening', connecting: 'Connecting', tool: 'Writing' } as Record<string, string>)[call.phase] || 'On the call' : busy === 'read' ? 'Reading' : busy ? 'Writing' : 'Your account manager at Beatly';
  const shown: Msg[] = msgs.length ? msgs : [{ role: 'lykke', text: opener }];

  return (
    <aside aria-label="Lykke, your account manager" className={cn('relative flex min-h-0 flex-col', inPanel ? 'flex-1' : 'bg-white', className)}>
      {!inPanel && <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
        <span className={cn('relative h-10 w-10 flex-none overflow-hidden rounded-full bg-[#2e024c]', call.on && 'ring-2 ring-[#a9f600] ring-offset-2')}>
          {call.on ? <video src={AGENT.video} poster={AGENT.photo} autoPlay loop muted playsInline className="h-full w-full object-cover" aria-hidden /> : <img src={AGENT.photo} alt="" className="h-full w-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>Lykke</div>
          <div className="truncate text-[12px] text-[#667085]">{status}</div>
        </div>
      </div>}

      <div ref={threadRef} className="flex min-h-[180px] flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 [scrollbar-width:none]" aria-live="polite">
        {shown.map((m, i) => m.role === 'user'
          ? <p key={i} className="max-w-[88%] self-end whitespace-pre-wrap rounded-2xl rounded-br-md bg-[#be43ff] px-3.5 py-2 text-[14px] leading-relaxed text-white">{m.text}</p>
          : (
            <div key={i} className="flex max-w-[92%] flex-col gap-1.5 self-start">
              <p className="whitespace-pre-wrap rounded-2xl rounded-bl-md bg-[var(--shell-bubble,#f2f4f7)] px-3.5 py-2 text-[14px] leading-relaxed text-[var(--shell-text,#101828)]">{m.text}</p>
              {m.applied?.map((a) => (
                <button key={a.id} type="button" onClick={() => onGo(a.step as Step)} className="inline-flex items-start gap-1.5 text-left text-[12px] text-[#067647] hover:underline">
                  <Check className="mt-px h-3.5 w-3.5 flex-none" aria-hidden /><span>Wrote {a.label}</span><span className="inline-flex items-center gap-0.5 text-[#667085]">{STEPS[a.step]}<ArrowRight className="h-3 w-3" aria-hidden /></span>
                </button>
              ))}
            </div>
          ))}
        {call.on && call.agentLive && <p className="max-w-[92%] self-start rounded-2xl bg-[#f9fafb] px-3.5 py-2 text-[14px] text-[#667085]">{call.agentLive}</p>}
        {call.on && call.live && <p className="max-w-[88%] self-end rounded-2xl bg-[#f9f5ff] px-3.5 py-2 text-[14px] italic text-[#667085]">{call.live}</p>}
        {busy && <p className="self-start rounded-2xl rounded-bl-md bg-[#f2f4f7] px-3.5 py-2 text-[13px] text-[#667085]"><Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" aria-hidden />{busy === 'read' ? 'Reading it' : 'Lykke is writing'}</p>}
      </div>

      <AgentComposer
        className={inPanel ? 'mx-3 mb-3' : 'border-t border-gray-100 px-3 pb-3 pt-2.5'}
        agentName="Lykke"
        busy={!!busy}
        filesAlone
        maxFiles={6}
        accept=".txt,.md,.csv,.pdf,.rtf,image/*,audio/*,video/*,text/*,application/pdf"
        acceptFile={(f) => /^(image|text|audio|video)\/|pdf|rtf/.test(f.type) || /\.(txt|md|csv|pdf|rtf|mp3|m4a|wav|mp4|mov|webm)$/i.test(f.name)}
        placeholder="Tell Lykke about the campaign, or drop a brief"
        onSend={(t, fs) => send(t, fs.map((f) => f.file))}
        call={speechSupported ? { phase: callPhase, caption: call.agentLive || call.live || undefined, start: () => void startCall(), end: endCall } : null}
      />
    </aside>
  );
}
